"use client";
import React, { useRef, useState, useEffect } from "react";
import { apiGetAllUpdatedKeywords, apiSearchResult } from "@/apis/user";
import { ellipse, getBaseImageUrl, getCategoryHref, getMerchantHref } from "@/constants/hooks";
import { MetaKeywordsResponse, SearchCategories, SearchMerchant } from "@/services/dataTypes";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { FaSearch, FaTimes, FaStore, FaTh, FaFire } from "react-icons/fa";

interface Props {
    companyId: string;
    mer_slug: string;
    slug_type: string;
    cat_slug: string;
    domain: string;
}

let debounceTimer: any = null;

const NavSearch = ({ companyId, mer_slug, slug_type, cat_slug, domain }: Props) => {
    const searchParams = useSearchParams();
    const params = new URLSearchParams(searchParams?.toString());
    const [search, setSearch] = useState<string>(params.get("query") || "");
    const [isOpen, setIsOpen] = useState(false);
    const [tagsData, setTagsData] = useState<MetaKeywordsResponse[]>([]);
    const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([]);
    const [merchantData, setMerchantData] = useState<SearchMerchant[]>([]);
    const [loading, setLoading] = useState(false);
    const pathName = usePathname();
    const router = useRouter();
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Load trending keywords once
    useEffect(() => {
        const load = async () => {
            const res = await apiGetAllUpdatedKeywords(domain);
            if (res?.data) setTagsData(res.data);
        };
        load();
    }, [companyId]);

    // Debounced search
    useEffect(() => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            if (search.trim().length >= 3) {
                setLoading(true);
                try {
                    const res = await apiSearchResult(search, companyId);
                    setCategoriesData(res.data?.categories || []);
                    setMerchantData(res.data?.merchants || []);
                } catch {
                    /* noop */
                } finally {
                    setLoading(false);
                }
            } else {
                setCategoriesData([]);
                setMerchantData([]);
            }
        }, 500);
    }, [search]);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Hide dropdown on /search page
    useEffect(() => {
        if (pathName === "/search") setIsOpen(false);
    }, [pathName]);

    const handleSubmit = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!search.trim()) return;
        setIsOpen(false);
        inputRef.current?.blur();
        router.push(`/search?query=${encodeURIComponent(search.trim())}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") handleSubmit();
        if (e.key === "Escape") { setIsOpen(false); inputRef.current?.blur(); }
    };

    const hasResults = merchantData.length > 0 || categoriesData.length > 0;
    const showDropdown = isOpen && (tagsData.length > 0 || hasResults || loading);

    return (
        <div ref={wrapperRef} className="relative w-full">
            {/* ── INPUT BAR ── */}
            <form
                onSubmit={handleSubmit}
                className={`flex items-center bg-white border transition-all duration-300 rounded-full px-3 py-2.5 gap-2 ${
                    isOpen
                        ? "border-blue-400 shadow-[0_0_0_3px_rgba(59,130,246,0.12)]"
                        : "border-slate-200 hover:border-slate-300"
                }`}
            >
                <FaSearch className={`shrink-0 text-[13px] transition-colors ${isOpen ? "text-blue-500" : "text-slate-400"}`} />

                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search stores, deals..."
                    className="flex-1 bg-transparent outline-none text-[13px] font-medium text-slate-700 placeholder-slate-400 min-w-0 py-1"
                />

                {search && (
                    <button
                        type="button"
                        onClick={() => { setSearch(""); setCategoriesData([]); setMerchantData([]); inputRef.current?.focus(); }}
                        className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
                    >
                        <FaTimes className="text-[10px]" />
                    </button>
                )}

                <button
                    type="submit"
                    className="shrink-0 bg-blue-600 hover:bg-slate-900 text-white text-[11px] font-black uppercase tracking-wider px-4 py-2 rounded-full transition-all duration-300 active:scale-95"
                >
                    Search
                </button>
            </form>

            {/* ── DROPDOWN ── */}
            {showDropdown && (
                <div className="absolute top-[calc(100%+8px)] left-0 w-full min-w-[420px] max-w-[520px] bg-white rounded-[20px] border border-slate-100 shadow-[0_20px_60px_rgba(0,0,0,0.12)] z-[99999] overflow-hidden">

                    {/* Loading state */}
                    {loading && (
                        <div className="px-5 py-4 flex items-center gap-3 border-b border-slate-50">
                            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
                            <span className="text-[11px] text-slate-400 font-medium">Searching...</span>
                        </div>
                    )}

                    {/* Results */}
                    {!loading && hasResults && (
                        <div className="max-h-[360px] overflow-y-auto">

                            {/* Merchants */}
                            {merchantData.length > 0 && (
                                <div className="p-4">
                                    <div className="flex items-center gap-1.5 mb-3">
                                        <FaStore className="text-blue-500 text-[9px]" />
                                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Stores</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {merchantData.slice(0, 6).map((item, i) => (
                                            <Link
                                                key={i}
                                                href={getMerchantHref<SearchMerchant>(item, mer_slug, slug_type)}
                                                onClick={() => setIsOpen(false)}
                                                className="no-underline flex items-center gap-2.5 p-2 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all group"
                                            >
                                                <div className="w-8 h-8 shrink-0 bg-white rounded-lg border border-slate-100 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform">
                                                    <Image
                                                        src={getBaseImageUrl(domain, item.merchant_logo, "")}
                                                        alt={item.merchant_name}
                                                        width={28}
                                                        height={28}
                                                        className="object-contain p-0.5"
                                                    />
                                                </div>
                                                <span className="text-[12px] font-semibold text-slate-700 group-hover:text-blue-600 transition-colors truncate">
                                                    {ellipse(item.merchant_name, 18)}
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Categories */}
                            {categoriesData.length > 0 && (
                                <div className={`p-4 ${merchantData.length > 0 ? "border-t border-slate-50" : ""}`}>
                                    <div className="flex items-center gap-1.5 mb-3">
                                        <FaTh className="text-blue-500 text-[9px]" />
                                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Categories</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {categoriesData.slice(0, 8).map((item, i) => (
                                            <Link
                                                key={i}
                                                href={getCategoryHref(item, cat_slug, slug_type)}
                                                onClick={() => setIsOpen(false)}
                                                className="no-underline px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-[11px] font-semibold text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                                            >
                                                {item.name}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* No results */}
                    {!loading && search.trim().length >= 3 && !hasResults && (
                        <div className="px-5 py-8 text-center">
                            <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                <FaSearch className="text-slate-300 text-sm" />
                            </div>
                            <p className="text-[12px] font-semibold text-slate-400">No results for &ldquo;{search}&rdquo;</p>
                        </div>
                    )}

                    {/* Trending tags — always shown when no search */}
                    {tagsData.length > 0 && (
                        <div className={`p-4 ${hasResults || (search.trim().length >= 3 && !hasResults) ? "border-t border-slate-50 bg-slate-50/50" : ""}`}>
                            <div className="flex items-center gap-1.5 mb-3">
                                <FaFire className="text-orange-400 text-[9px]" />
                                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Trending</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {tagsData.slice(0, 8).map((item: MetaKeywordsResponse, i: number) => (
                                    <Link
                                        key={i}
                                        href={item?.merchant ? getMerchantHref(item.merchant, mer_slug, slug_type) : `/search?query=${item?.keyword}`}
                                        onClick={() => setIsOpen(false)}
                                        className="no-underline px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-all shadow-sm"
                                    >
                                        #{item?.keyword}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Footer — view all */}
                    {search.trim() && (
                        <div className="px-4 py-2.5 bg-slate-900 flex items-center justify-between">
                            <span className="text-[9px] text-slate-500 font-medium uppercase tracking-wider truncate">
                                Results for &ldquo;{search}&rdquo;
                            </span>
                            <button
                                onClick={() => handleSubmit()}
                                className="text-[9px] font-black uppercase tracking-wider text-blue-400 hover:text-white transition-colors whitespace-nowrap ml-4"
                            >
                                View All →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NavSearch;
